import { config, passport } from '@imtbl/sdk';

// Hardcoded values for production
const CLIENT_ID = '3XNt4DTQmsmgllhZuItkOfH4gtR4m7Gk';
const PUBLISHABLE_KEY = 'pk_imapik-mInFD7xqeVpe_MU$DnMh';
const ENVIRONMENT = config.Environment.SANDBOX;

// Hardcoded redirect URIs to match exactly what's configured in the Immutable Hub
const REDIRECT_URI = 'https://app.bitsport.gg/passport-callback';
const LOGOUT_REDIRECT_URI = 'https://app.bitsport.gg/passport-logout';

console.log('Immutable Passport Configuration:');
console.log('Client ID:', CLIENT_ID);
console.log('Publishable Key:', PUBLISHABLE_KEY);
console.log('Environment:', ENVIRONMENT === config.Environment.PRODUCTION ? 'Production' : 'Sandbox');
console.log('Redirect URI:', REDIRECT_URI);
console.log('Logout Redirect URI:', LOGOUT_REDIRECT_URI);

// Base configuration for Immutable SDK
const baseConfig = {
  environment: ENVIRONMENT,
  publishableKey: PUBLISHABLE_KEY,
};

// Create Passport instance with hardcoded redirect URLs
let passportInstance;
try {
  passportInstance = new passport.Passport({
    baseConfig,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    logoutRedirectUri: LOGOUT_REDIRECT_URI,
    audience: 'platform_api',
    scope: 'openid offline_access email transact',
    popupOverlayOptions: {
      disableGenericPopupOverlay: false,
      disableBlockedPopupOverlay: false,
    }
  });
  console.log('Passport instance created successfully');
} catch (error) {
  console.error('Error creating Passport instance:', error);
}

/**
 * Function to get the Passport instance
 * This ensures that components can access the same instance
 */
export const getPassportInstance = () => {
  return passportInstance;
};

/**
 * Ensure AppKit doesn't interfere with Passport
 * This function should be called before any Passport operations
 */
const ensurePassportProvider = () => {
  // Mark that we're using Passport
  localStorage.setItem('wallet_provider', 'passport');

  // Clear any AppKit auto-reconnect flags
  localStorage.removeItem('wallet_reconnect_requested');

  // Don't remove other wallet data as it might be needed for the session
};

/**
 * Helper function to connect to Passport EVM provider
 * This establishes the connection to the Immutable blockchain
 */
export const connectPassportProvider = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    console.log('Connecting to Passport EVM provider...');
    // Connect to the Passport EVM provider
    const provider = await passportInstance.connectEvm();
    console.log('Passport EVM provider connected successfully');

    // Add custom error handling for the provider
    const originalRequest = provider.request;
    provider.request = async (args) => {
      try {
        return await originalRequest.call(provider, args);
      } catch (error) {
        // Handle network-related errors
        if (error.message && error.message.includes('network')) {
          console.error('Network error in Passport provider:', error);
          // Don't let AppKit try to handle this
          error.isPassportError = true;
          // Provide a more helpful error message
          throw new Error(`Immutable Passport network error: ${error.message}. Please ensure you're connected to the correct network.`);
        }
        throw error;
      }
    };

    return provider;
  } catch (error) {
    console.error('Error connecting to Passport provider:', error);
    // Add more context to the error
    if (error.message && error.message.includes('popup')) {
      throw new Error(`Passport login popup was blocked. Please allow popups for this site and try again. Original error: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Helper function to login with Passport
 * This handles the full authentication flow
 */
export const loginWithPassport = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    console.log('Starting Passport login process...');

    // First check if user is already logged in
    try {
      const existingUserInfo = await passportInstance.getUserInfo();
      if (existingUserInfo) {
        console.log('User already logged in with Passport:', existingUserInfo);

        // Connect to provider to get the address
        console.log('Connecting to existing Passport provider...');
        const provider = await connectPassportProvider();
        console.log('Provider connected:', provider);

        console.log('Requesting accounts...');
        const accounts = await provider.request({ method: 'eth_requestAccounts' });
        console.log('Accounts received:', accounts);

        // Store user info in localStorage for persistence
        if (accounts && accounts.length > 0) {
          const address = accounts[0];
          localStorage.setItem('passport_user_info', JSON.stringify(existingUserInfo));
          localStorage.setItem('passport_address', address);
        }

        return { provider, accounts, userInfo: existingUserInfo };
      }
    } catch (existingUserError) {
      console.log('No existing Passport session found, proceeding with login');
    }

    console.log('Connecting to Passport provider...');
    const provider = await connectPassportProvider();
    console.log('Provider connected:', provider);

    console.log('Requesting accounts...');
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    console.log('Accounts received:', accounts);

    // Try to get user info
    let userInfo = null;
    try {
      userInfo = await passportInstance.getUserInfo();
      console.log('User info:', userInfo);

      // Store user info in localStorage for persistence
      if (userInfo && accounts && accounts.length > 0) {
        const address = accounts[0];
        localStorage.setItem('passport_user_info', JSON.stringify(userInfo));
        localStorage.setItem('passport_address', address);
      }
    } catch (userInfoError) {
      console.warn('Could not get user info:', userInfoError);
    }

    return { provider, accounts, userInfo };
  } catch (error) {
    console.error('Error logging in with Passport:', error);
    if (error.message) {
      console.error('Error message:', error.message);
    }
    if (error.stack) {
      console.error('Error stack:', error.stack);
    }

    // If there's a network error, provide a more helpful message
    if (error.message && error.message.includes('network')) {
      console.error('Network error detected. This might be due to an incompatible network configuration.');
      // Add a custom property to the error to help identify it
      error.isNetworkError = true;
      throw new Error(`Network error connecting to Immutable. Please ensure you're connected to the correct network. Original error: ${error.message}`);
    }

    throw error;
  }
};

/**
 * Helper function to get user info from Passport
 * This can be used to check if the user is logged in
 */
export const getPassportUserInfo = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    const userInfo = await passportInstance.getUserInfo();
    return userInfo;
  } catch (error) {
    console.error('Error getting user info from Passport:', error);
    return null;
  }
};

/**
 * Helper function to logout from Passport
 * This handles the full logout flow
 */
export const logoutFromPassport = async () => {
  try {
    console.log('Logging out from Passport...');

    // Clear Passport-specific localStorage items first
    localStorage.removeItem('passport_user_info');
    localStorage.removeItem('passport_address');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Only remove wallet_provider if it's set to passport
    if (localStorage.getItem('wallet_provider') === 'passport') {
      localStorage.removeItem('wallet_provider');
    }

    // Clear any reconnect flags
    localStorage.removeItem('wallet_reconnect_requested');
    localStorage.removeItem('wallet_connected');

    try {
      // Get the logout URL from the Passport instance
      const logoutUrl = passportInstance.getLogoutUrl();
      console.log('Passport logout URL:', logoutUrl);

      // Redirect to the logout URL
      window.location.href = logoutUrl;
      return true;
    } catch (error) {
      console.error('Error getting logout URL:', error);
      return false;
    }
  } catch (error) {
    console.error('Error in Passport logout process:', error);
    return false;
  }
};

export { passportInstance };
